import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { O2OOrderService } from '../services/o2o-order.service';
import { O2OOrder, O2OSearchParams } from '@models/o2o-order';
import {
  DataTableComponent,
  TableColumn,
  PaginationEvent,
} from '@shared/components/data-table/data-table.component';
import {
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';
import {
  StatusFilterComponent,
  StatusOption,
  ActionButton,
} from '@shared/components/status-filter/status-filter.component';
import { O2O_INTERNAL_STATUS_OPTIONS } from '../services/o2o.config';

@Component({
  selector: 'app-o2o-audit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DataTableComponent,
    SearchFormComponent,
    StatusFilterComponent,
  ],
  templateUrl: './o2o-audit.component.html',
  styleUrl: './o2o-audit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class O2OAuditComponent implements OnInit, OnDestroy {
  private o2oService = inject(O2OOrderService);
  private destroy$ = new Subject<void>();

  // Signals for dropdown options
  orderSources = signal<string[]>([]);
  salesChannels = signal<string[]>([]);
  buyerNotesOptions = signal<string[]>([]);
  giftOptions = signal<string[]>([]);

  // Table data
  selectedOrders = signal<O2OOrder[]>([]);
  loading = signal(false);
  orders = signal<O2OOrder[]>([]);
  totalRecords = signal(0);

  // Pagination
  currentPage = signal(1);
  pageSize = signal(20);
  first = signal(0);

  // Status filter options
  statusOptions: StatusOption[] = O2O_INTERNAL_STATUS_OPTIONS;

  // Action buttons
  primaryButton: ActionButton = {
    label: 'o2o.audit.actions.approve',
    severity: 'info',
    disabled: false,
    show: true,
  };

  secondaryButton: ActionButton = {
    label: 'o2o.audit.actions.reject',
    severity: 'info',
    outlined: true,
    disabled: false,
    show: true,
  };

  // Table columns for DataTableComponent
  tableColumns: TableColumn[] = [
    {
      label: 'o2o.audit.table.third_party_order_no',
      prop: 'thirdPartyOrderNo',
      width: 120,
    },
    { label: 'o2o.audit.table.tags', prop: 'tags', width: 100 },
    {
      label: 'o2o.audit.table.internal_order_no',
      prop: 'internalOrderNo',
      width: 120,
    },
    { label: 'o2o.audit.table.audit_status', prop: 'auditStatus', width: 100 },
    { label: 'o2o.audit.table.sales_store', prop: 'salesStore', width: 120 },
    {
      label: 'o2o.audit.table.sales_channel',
      prop: 'salesChannel',
      width: 120,
    },
    {
      label: 'o2o.audit.table.sales_employee',
      prop: 'salesEmployee',
      width: 100,
    },
    { label: 'o2o.audit.table.pickup_store', prop: 'pickupStore', width: 120 },
    { label: 'o2o.audit.table.order_source', prop: 'orderSource', width: 100 },
    {
      label: 'o2o.audit.table.product_lines',
      prop: 'productLineCount',
      width: 80,
    },
    {
      label: 'o2o.audit.table.buyer_nickname',
      prop: 'buyerNickname',
      width: 100,
    },
    {
      label: 'o2o.audit.table.buyer_remarks',
      prop: 'buyerRemarks',
      width: 100,
    },
    {
      label: 'o2o.audit.table.merchant_remarks',
      prop: 'merchantRemarks',
      width: 100,
    },
    {
      label: 'o2o.audit.table.pickup_method',
      prop: 'pickupMethod',
      width: 100,
    },
    {
      label: 'o2o.audit.table.recipient_name',
      prop: 'recipientName',
      width: 100,
    },
    {
      label: 'o2o.audit.table.recipient_phone',
      prop: 'recipientPhone',
      width: 120,
    },
    {
      label: 'o2o.audit.table.recipient_address',
      prop: 'recipientAddress',
      width: 150,
    },
    { label: 'o2o.audit.table.total_amount', prop: 'totalAmount', width: 100 },
    {
      label: 'o2o.audit.table.buyer_payable',
      prop: 'buyerPayable',
      width: 100,
    },
    { label: 'o2o.audit.table.total_cost', prop: 'totalCost', width: 100 },
    {
      label: 'o2o.audit.table.third_party_order_time',
      prop: 'thirdPartyOrderTime',
      width: 150,
    },
    { label: 'o2o.audit.table.payment_time', prop: 'paymentTime', width: 150 },
    {
      label: 'o2o.audit.table.internal_create_time',
      prop: 'internalCreateTime',
      width: 150,
    },
    { label: 'o2o.audit.table.has_gift', prop: 'hasGift', width: 80 },
  ];

  // Computed properties
  canApprove = computed(() => this.selectedOrders().length > 0);
  canReject = computed(() => this.selectedOrders().length > 0);

  // Dialog state
  showRejectDialog = signal(false);
  rejectReason = signal('');

  ngOnInit(): void {
    this.loadDropdownOptions();
    this.loadOrders();
    this.updateButtonStates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadDropdownOptions(): void {
    // Load dropdown options from service
    // this.o2oService.getOrderSources().subscribe(sources => {
    //   this.orderSources.set(sources);
    // });

    // this.o2oService.getSalesChannels().subscribe(channels => {
    //   this.salesChannels.set(channels);
    // });

    // Set static options
    this.buyerNotesOptions.set([
      'o2o.audit.options.all',
      'o2o.audit.options.has_remarks',
      'o2o.audit.options.no_remarks',
    ]);
    this.giftOptions.set([
      'o2o.audit.options.all',
      'o2o.audit.options.yes',
      'o2o.audit.options.no',
    ]);
  }

  private loadOrders(): void {
    this.loading.set(true);

    const searchParams: O2OSearchParams = {
      auditStatus: this.getActiveStatus(),
      page: this.currentPage(),
      pageSize: this.pageSize(),
    };

    this.o2oService.searchOrders(searchParams).subscribe({
      next: (result) => {
        this.orders.set(result.orders);
        this.totalRecords.set(result.total);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading orders:', error);
        this.loading.set(false);
      },
    });
  }

  private getActiveStatus():
    | 'all'
    | 'incomplete'
    | 'completed'
    | 'closed'
    | 'exception' {
    const activeStatus = this.statusOptions.find((status) => status.active);
    return (activeStatus ? activeStatus.key : 'all') as
      | 'all'
      | 'incomplete'
      | 'completed'
      | 'closed'
      | 'exception';
  }

  private updateButtonStates(): void {
    this.primaryButton.disabled = !this.canApprove();
    this.secondaryButton.disabled = !this.canReject();
  }

  onSearch(searchData: SearchFormData): void {
    this.currentPage.set(1);
    this.first.set(0);
    // Convert search data to search params and load orders
    this.loadOrders();
  }

  onReset(): void {
    this.selectedOrders.set([]);
    this.currentPage.set(1);
    this.first.set(0);
    this.loadOrders();
    this.updateButtonStates();
  }

  onPageChange(event: PaginationEvent): void {
    this.currentPage.set(event.page! + 1);
    this.first.set(event.first || 0);
    this.loadOrders();
  }

  onStatusChange(status: string): void {
    // Update status options
    this.statusOptions = this.statusOptions.map((option) => ({
      ...option,
      active: option.key === status,
    }));

    this.currentPage.set(1);
    this.first.set(0);
    this.loadOrders();
  }

  onSelectionChange(selection: O2OOrder[]): void {
    this.selectedOrders.set(selection);
    this.updateButtonStates();
  }

  onApprove(): void {
    if (this.selectedOrders().length === 0) return;

    const orderIds = this.selectedOrders().map((order) => order.id);
    this.o2oService.auditOrders({ orderIds, action: 'approve' }).subscribe({
      next: (result: boolean) => {
        if (result) {
          this.selectedOrders.set([]);
          this.loadOrders();
          this.updateButtonStates();
        }
      },
      error: (error: any) => {
        console.error('Error approving orders:', error);
      },
    });
  }

  onReject(): void {
    if (this.selectedOrders().length === 0) return;
    this.showRejectDialog.set(true);
  }

  confirmReject(): void {
    if (this.selectedOrders().length === 0 || !this.rejectReason()) return;

    const orderIds = this.selectedOrders().map((order) => order.id);
    this.o2oService
      .auditOrders({
        orderIds,
        action: 'reject',
        reason: this.rejectReason(),
      })
      .subscribe({
        next: (result: boolean) => {
          if (result) {
            this.selectedOrders.set([]);
            this.rejectReason.set('');
            this.showRejectDialog.set(false);
            this.loadOrders();
            this.updateButtonStates();
          }
        },
        error: (error: any) => {
          console.error('Error rejecting orders:', error);
        },
      });
  }

  cancelReject(): void {
    this.rejectReason.set('');
    this.showRejectDialog.set(false);
  }

  refreshData(): void {
    this.loadOrders();
  }
}
